import {
  sidebarToolsIcon,
  sidebarBusinessScansIcon,
  sidebarTransactionsIcon,
  sidebarPurchaseLinksIcon,
  sidebarPaymentErrorsIcon,
  sidebarReportsIcon,
  sidebarScheduledReportIcon,
  sidebarBundleCouponIcon,
  pendingActionsIcon,
  taskAltIcon,
  settingsIcon,
  couponIcon,
  iconcasestudies,
} from '../../assets'
import {NAV_ICONS} from './nav-icons'
import {getAuthByRole} from '../../utils/get-screen-auth-by-role'
import {AUTHORIZED_ROLES_BY_SCREENS} from '../../utils/constants'
import {getAccountManagementLandingPath} from '../../utils/account-management-navigation'

/**
 * Item shape:
 *   {id, label, icon, activeIcon?, to, exactPaths?, pathPrefixes?, children?, disabled?}
 * - `to` is null for groups (items with `children`).
 * - `exactPaths` / `pathPrefixes` extend the pathname match beyond `to`
 *   (e.g. Appointments is also active on /new-demo-request).
 */

// Mirrors setAppointmentsSubLinks() in components/side-navbar.js: the
// Appointments entry shows when the role can see Demo Requests or Onboarding.
export function canSeeAppointments(roles) {
  const {demoRequests, appointments} = AUTHORIZED_ROLES_BY_SCREENS
  return (
    getAuthByRole(demoRequests, roles) || getAuthByRole(appointments, roles)
  )
}

/**
 * Pure visibility rules for the side nav (same rules as side-navbar.js).
 * Items are returned in display order.
 *
 * @param {{roles: string[], features: object}} params
 * @returns {object[]}
 */
export function buildNavItems({roles = [], features = {}}) {
  const {
    Business_Scan_Access: hasBusinessScanAccess,
    Orders_Menu_Access: hasOrdersLinkAccess,
    MANAGE_PRODUCT_RULES: hasManageProductRulesAccess,
    Coupon_Config_Access: hasCouponConfigAccess,
    Proposal_Tool_Access: hasProposalToolAccess,
    Feature_Flags_Read_Access: hasFeatureFlagsReadAccess,
    proposal_metrics: hasProposalMetricsAccess,
    Purchase_Links_Dashboard_Access: hasPurchaseLinksDashboardAccess,
    Payment_Errors_Dashboard_Access: hasPaymentErrorsDashboardAccess,
  } = features

  const items = []

  // Account Management — always. `yello` role sees it labelled "My Accounts".
  items.push({
    id: 'accounts',
    label: roles.includes('yello') ? 'My Accounts' : 'Account Management',
    ...NAV_ICONS.accounts,
    to: getAccountManagementLandingPath(roles),
    exactPaths: ['/'],
    pathPrefixes: ['/accounts'],
  })

  if (canSeeAppointments(roles)) {
    items.push({
      id: 'appointments',
      label: 'Appointments',
      ...NAV_ICONS.appointments,
      to: '/demo-requests',
      exactPaths: ['/new-demo-request'],
    })
  }

  // Always visible
  items.push(
    {
      id: 'collateral',
      label: 'Collateral & Education',
      ...NAV_ICONS.collateral,
      to: '/tools',
    },
    {
      id: 'pricing-calculator',
      label: '4.0 Pricing Calculator',
      ...NAV_ICONS.calculator,
      to: '/price-calculator',
    },
  )

  if (hasBusinessScanAccess) {
    items.push({
      id: 'business-scans',
      label: 'Business Scans',
      icon: sidebarBusinessScansIcon,
      to: '/scans',
    })
  }

  if (getAuthByRole(AUTHORIZED_ROLES_BY_SCREENS.adminClientCenter, roles)) {
    items.push({
      id: 'my-client-center',
      label: 'My Client Center',
      ...NAV_ICONS.clientCenter,
      to: '/my-client-center',
    })
  }

  if (hasOrdersLinkAccess) {
    items.push({
      id: 'purchase-links',
      label: 'Purchase Links',
      ...NAV_ICONS.purchaseLinks,
      to: null,
      children: [
        {
          id: 'pending-links',
          label: 'Pending',
          icon: pendingActionsIcon,
          to: '/pending-links',
        },
        {
          id: 'completed-links',
          label: 'Completed',
          icon: taskAltIcon,
          to: '/completed-links',
        },
      ],
    })
  }

  if (hasPurchaseLinksDashboardAccess || hasPaymentErrorsDashboardAccess) {
    const children = []
    if (hasPurchaseLinksDashboardAccess) {
      children.push({
        id: 'purchase-links-dashboard',
        label: 'Purchase Links',
        icon: sidebarPurchaseLinksIcon,
        to: '/purchase-links-dashboard',
      })
    }
    if (hasPaymentErrorsDashboardAccess) {
      children.push({
        id: 'payment-errors-dashboard',
        label: 'Payment Errors',
        icon: sidebarPaymentErrorsIcon,
        to: '/payment-errors-dashboard',
      })
    }
    items.push({
      id: 'transaction-logs',
      label: 'Transaction Logs',
      icon: sidebarTransactionsIcon,
      to: null,
      children,
    })
  }

  // Configuration group is hidden when the user has no configuration sublinks.
  const configChildren = []
  if (hasCouponConfigAccess) {
    configChildren.push({
      id: 'coupons-configuration',
      label: 'Coupons Configuration',
      icon: couponIcon,
      to: '/coupons',
    })
  }
  if (hasManageProductRulesAccess) {
    configChildren.push({
      id: 'manage-product-rules',
      label: 'Manage Product Rules',
      icon: iconcasestudies,
      to: '/manage-product-rules',
    })
  }
  if (configChildren.length > 0) {
    items.push({
      id: 'configuration',
      label: 'Configuration',
      icon: settingsIcon,
      to: null,
      children: configChildren,
    })
  }

  if (hasFeatureFlagsReadAccess) {
    items.push({
      id: 'feature-flags',
      label: 'Feature Flags',
      icon: sidebarToolsIcon,
      to: '/features',
    })
  }

  // Reports group appears if the user can see any real sublink. Coupon Report
  // is role-gated (also enforced server-side), Proposal Metrics is flag-gated,
  // Scheduled Report is a disabled placeholder.
  const canSeeBundleCouponReport = getAuthByRole(
    AUTHORIZED_ROLES_BY_SCREENS.bundleCouponReport,
    roles,
  )
  if (canSeeBundleCouponReport || hasProposalMetricsAccess) {
    const children = [
      {
        id: 'scheduled-report',
        label: 'Scheduled Report',
        icon: sidebarScheduledReportIcon,
        to: null,
        disabled: true,
      },
    ]
    if (canSeeBundleCouponReport) {
      children.push({
        id: 'bundle-coupon-report',
        label: 'Coupon Report',
        icon: sidebarBundleCouponIcon,
        to: '/reports/bundle-coupon',
      })
    }
    if (hasProposalMetricsAccess) {
      children.push({
        id: 'proposal-metrics',
        label: 'Proposal Metrics',
        icon: sidebarReportsIcon,
        to: '/reports/proposal-metrics',
      })
    }
    items.push({
      id: 'reports',
      label: 'Reports',
      ...NAV_ICONS.reports,
      to: null,
      children,
    })
  }

  if (hasProposalToolAccess) {
    items.push({
      id: 'proposal-tool',
      label: 'Proposal Tool',
      ...NAV_ICONS.proposalTool,
      to: '/proposal-tool',
      pathPrefixes: ['/proposal-tool'],
    })
  }

  return items
}

const matchesPath = (pathname, path) => pathname === path

const matchesPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

// `locationState` is only consulted by items with `panel` (Control Center
// views share one route and are told apart by location.state.activePanel;
// `defaultPanel` is the one shown when no panel was requested).
export function isItemActive(item, pathname, locationState) {
  if (item.children) {
    return item.children.some(child =>
      isItemActive(child, pathname, locationState),
    )
  }
  if (item.disabled) return false
  if (item.panel) {
    return (
      matchesPath(pathname, item.to) &&
      (locationState?.activePanel || item.defaultPanel) === item.panel
    )
  }
  return (
    (item.to != null && matchesPath(pathname, item.to)) ||
    (item.exactPaths || []).some(p => matchesPath(pathname, p)) ||
    (item.pathPrefixes || []).some(p => matchesPrefix(pathname, p))
  )
}
